import { useState, useEffect, useMemo } from 'react'
import { Link2 } from 'lucide-react'

import FbButton from 'components/FbUI/FbButton'
import useApiService from 'services/api'
import { AccountCard } from './AccountCard'
import { toast } from 'react-toastify'
import { cn } from 'components/UI/cn'
import { formatDate, formatDecimalOnly } from 'utils/formatting'
import { API_HOST } from 'utils/http-client'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export function Account({ disabled }: { disabled?: boolean }) {
  const api = useApiService()
  const featureFlag = useFeatureFlagService()
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingStripe, setIsLoadingStripe] = useState(false)
  const [newRebateEmail, setNewRebateEmail] = useState('')
  const [newBillingEmail, setNewBillingEmail] = useState('')
  const [newSampleCustomerServiceEmail, setNewSampleCustomerServiceEmail] =
    useState('')
  const { data: companyData, isLoading, refetch } = api.useGetCompanyDetails()
  const primaryLogo = useMemo(() => {
    return companyData?.logos.find((logo) => logo.is_primary)
  }, [companyData])

  useEffect(() => {
    if (companyData?.billing_email) {
      setNewBillingEmail(companyData.billing_email)
    }
    if (companyData?.rebate_support_email) {
      setNewRebateEmail(companyData.rebate_support_email)
    }
    if (companyData?.sample_customer_service_email) {
      setNewSampleCustomerServiceEmail(
        companyData.sample_customer_service_email
      )
    }
  }, [companyData])

  async function openStripePortal() {
    if (disabled) return
    if (!companyData?.stripe_customer_id) {
      toast.error('Billing email is required to create a Stripe customer')
      return
    }

    setIsLoadingStripe(true)
    try {
      const url = await api.getStripePortalRedirect(window.location.href)
      window.location.href = url
    } finally {
      setIsLoadingStripe(false)
    }
  }

  async function handleSaveCompanyDetails() {
    if (disabled) return
    setIsSaving(true)
    try {
      const previousSampleEmail = companyData?.sample_customer_service_email
      await api.updateCompanyDetails({
        billingEmail: newBillingEmail,
        rebateSupportEmail: newRebateEmail,
        sampleCustomerServiceEmail: newSampleCustomerServiceEmail,
      })
      refetch({ cancelRefetch: false })

      // Show success message if sample customer service email was updated
      if (
        newSampleCustomerServiceEmail &&
        newSampleCustomerServiceEmail !== previousSampleEmail
      ) {
        toast.success(
          `Test email sent to ${newSampleCustomerServiceEmail}. Check your inbox to confirm.`
        )
      }
    } catch (error: any) {
      console.error('Error saving company details:', error)
      // Check if it's a test email error
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error)
      } else {
        toast.error('Failed to save company details. Please try again.')
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col w-full">
      {isLoading || isLoadingStripe ? (
        <div className="text-center text-gray-600 py-6">
          Loading account informations...
        </div>
      ) : (
        <>
          <div className="flex items-center mt-4">
            {primaryLogo ? (
              <img
                className="rounded-full w-10 h-10 mr-2"
                src={new URL(primaryLogo?.logo_url ?? '', API_HOST).href}
                alt={primaryLogo?.original_filename}
              />
            ) : (
              <div className="rounded-full bg-fb-green-900 w-10 h-10 flex items-center justify-center text-white text-lg font-bold mr-2">
                {companyData?.name[0]}
              </div>
            )}

            <h1 className="text-2xl font-semibold mr-6">{companyData?.name}</h1>
            <a
              href={`https://${companyData?.email_extensions[0].email_extension}`}
              target="_blank"
              className="flex items-center text-sm text-[#5D6B98] gap-1 border-1 font-medium border-[#B9C0D4] rounded-full px-2 py-1 mr-6"
              rel="noreferrer"
            >
              <Link2 className="text-[#5D6B98]" size={16} />{' '}
              {companyData?.email_extensions[0].email_extension}
            </a>
            {companyData && companyData.activate_type !== 'none' ? (
              <div
                className={cn(
                  'flex items-center text-sm font-semibold rounded-full px-2 py-1 bg-surface-active text-brand-pressed'
                )}
              >
                Activate
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex">
            <AccountCard
              label="Active Since"
              value={formatDate(companyData?.contract_start_date)}
            />

            <AccountCard
              label="Annual Contact Credits"
              value={formatDecimalOnly(
                companyData?.annual_contact_credits ?? 0
              )}
              className="w-56"
            />

            {featureFlag.enableUserTypes &&
              companyData?.seat_buckets?.map((bucket) => (
                <AccountCard
                  key={bucket.user_types.join(',')}
                  label={`${bucket.user_types.join(' / ')} Seats`}
                  value={`${bucket.active}/${bucket.limit}`}
                />
              ))}
          </div>

          <div className="mt-6 px-1">
            <div>
              <label
                htmlFor="billing-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Billing Email
              </label>
              <div className="flex items-center">
                <input
                  id="billing-email"
                  type="email"
                  value={newBillingEmail}
                  onChange={(e) => setNewBillingEmail(e.target.value)}
                  disabled={
                    disabled ||
                    !!(
                      companyData?.billing_email &&
                      newBillingEmail === companyData?.billing_email
                    )
                  }
                  placeholder="Enter billing email"
                  className="w-60 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fb-dark-green focus:border-darring-fb-dark-green"
                />
                <button
                  className="underline text-brand-active flex justify-center items-center ml-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => openStripePortal()}
                  disabled={disabled}
                >
                  Manage Billing
                </button>
              </div>
            </div>

            <div className="mt-8">
              <label
                htmlFor="rebates-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Rebates Email
              </label>
              <input
                id="rebates-email"
                type="email"
                value={newRebateEmail}
                onChange={(e) => setNewRebateEmail(e.target.value)}
                disabled={disabled}
                placeholder="Enter rebates email"
                className="w-60 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fb-dark-green focus:border-darring-fb-dark-green"
              />
            </div>

            {featureFlag.enableSamples && (
              <div className="mt-8">
                <label
                  htmlFor="sample-customer-service-email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Sample Customer Service Email
                </label>
                <input
                  id="sample-customer-service-email"
                  type="email"
                  value={newSampleCustomerServiceEmail}
                  onChange={(e) =>
                    setNewSampleCustomerServiceEmail(e.target.value)
                  }
                  disabled={disabled}
                  placeholder="Enter sample customer service email"
                  className="w-60 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fb-dark-green focus:border-darring-fb-dark-green"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email address where sample request notifications will be sent
                </p>
              </div>
            )}
            {disabled ? null : !(
                newRebateEmail !== (companyData?.rebate_support_email ?? '') ||
                (featureFlag.enableSamples &&
                  newSampleCustomerServiceEmail !==
                    (companyData?.sample_customer_service_email ?? '')) ||
                (newBillingEmail !== (companyData?.billing_email ?? '') &&
                  !companyData?.billing_email)
              ) ? null : (
              <div className="mt-6">
                <FbButton
                  onClick={handleSaveCompanyDetails}
                  loading={isSaving}
                  disabled={
                    !(
                      newRebateEmail !==
                        (companyData?.rebate_support_email ?? '') ||
                      (featureFlag.enableSamples &&
                        newSampleCustomerServiceEmail !==
                          (companyData?.sample_customer_service_email ?? '')) ||
                      (newBillingEmail !== (companyData?.billing_email ?? '') &&
                        !companyData?.billing_email)
                    )
                  }
                  size="md"
                  className="w-24"
                >
                  Save
                </FbButton>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
